/**
 * Trees, props, water, roads and buildings for one vector chunk.
 *
 * Flora is one `InstancedMesh` per species present — pine and birch in Sweden,
 * acacia and dry scrub on the savanna — so a tile's worth of landscape is a
 * handful of draw calls rather than thousands. See `flora.ts` for the models
 * and `core/biome/flora.ts` for what grows where.
 */

import type { InstancedMesh } from 'three';
import type { WindState } from '../core/weather/wind.js';
import {
  DoubleSide,
  Color,
  Sphere,
  Vector2,
  Vector3,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  BufferAttribute,
  BufferGeometry,
} from 'three';
import { createHouseMeshes, disposeHouseChunk } from './houseChunk.js';
import { GLSL_LAMP_LIGHT, buildFlora, buildLightPools, floraNight, lampLight } from './flora.js';
import { buildSigns, disposeSigns } from './signs.js';
import { buildHelipads } from './helipadMesh.js';
import { createRoadMaterial } from './roadMaterial.js';
import { createSurfaceMaterial } from './surfaceMaterial.js';
import { CLEARED_LAYER, SURFACE_LAYER } from './layers.js';
import type { PlacedVectorChunk } from '../core/build/placement.js';
import { biomeWeights } from '../core/biome/biome.js';
import { seaLevelClimate } from '../core/biome/climate.js';
import { blendedTone } from '../core/biome/palette.js';
import { tileBounds } from '../core/geo/tilekey.js';

/** Roads, paths and railways: surfaces and markings drawn in the shader. */
export const roadMaterial = createRoadMaterial();

/** Pitches, courts and pools, markings drawn in the shader. */
const surfaceMaterial = createSurfaceMaterial();

/** Inside a tunnel: no sun reaches in, the lamps and the concrete carry their own light. */
const tunnelMaterial = new MeshBasicMaterial({ vertexColors: true, side: DoubleSide });

const bridgeMaterial = new MeshStandardMaterial({
  color: new Color(0xffffff),
  vertexColors: true,
  roughness: 0.9,
  metalness: 0,
  side: DoubleSide,
  flatShading: true,
});
bridgeMaterial.onBeforeCompile = (shader) => {
  shader.uniforms.uNight = floraNight;
  shader.uniforms.uLampMap = lampLight.map;
  shader.uniforms.uLampOrigin = lampLight.origin;
  shader.uniforms.uLampInvExtent = lampLight.invExtent;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nvarying vec2 vBridgeWorld;')
    .replace('#include <worldpos_vertex>', '#include <worldpos_vertex>\n  vBridgeWorld = (modelMatrix * vec4(transformed, 1.0)).xz;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>\nuniform float uNight;\nvarying vec2 vBridgeWorld;\n${GLSL_LAMP_LIGHT}`)
    .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>\n  totalEmissiveRadiance += streetLampRadiance(vBridgeWorld, diffuseColor.rgb, uNight, 0.0);`);
};

const pierMaterial = new MeshStandardMaterial({
  color: new Color(0x9a7651),
  roughness: 0.95,
  metalness: 0,
  side: DoubleSide,
  flatShading: true,
  polygonOffset: true,
  polygonOffsetFactor: -1,
  polygonOffsetUnits: -1,
});

export const waterMaterial = new MeshStandardMaterial({
  color: new Color(0x43b9d1),
  roughness: 0.48,
  metalness: 0,
  transparent: true,
  opacity: 0.96,
  side: DoubleSide,
});

/**
 * The water's motion, as colour cycling: the surface stays still and flat,
 * and its colour steps through a few tones in bands that drift downwind,
 * with a glint here and there flashing in turn. A few instructions a pixel
 * and four uniforms a frame: the water's polygons are a handful of big
 * triangles from the map, too coarse to carry waves in their vertices.
 * The bands' phase wanders with a slow noise, so a harbour does not pulse
 * all at once.
 */
const waterUniforms = {
  uWaterTime: { value: 0 },
  /** How far the bands have drifted, metres. */
  uWaterShift: { value: new Vector2() },
  uWaterDir: { value: new Vector2(1, 0) },
  /** 0 in a calm to 1 in a fresh breeze: how strongly the bands show. */
  uWaterRough: { value: 0 },
};

/** How fast the bands drift, m/s: slowly even in a calm, a little faster with the wind. */
const BAND_SPEED = 0.25, BAND_PER_WIND = 0.05;
/** The wind, m/s, at which the bands show at full strength. */
const ROUGH_WIND = 8;
let waterClock: number | undefined;

/** Hand the water this frame's wind. */
export function setWaterWind(wind: WindState): void {
  const dt = waterClock === undefined ? 0 : Math.min(0.25, Math.max(0, wind.time - waterClock));
  waterClock = wind.time;
  const speed = BAND_SPEED + BAND_PER_WIND * Math.min(15, wind.speed);
  waterUniforms.uWaterShift.value.x += wind.dir.x * speed * dt;
  waterUniforms.uWaterShift.value.y += wind.dir.z * speed * dt;
  waterUniforms.uWaterDir.value.set(wind.dir.x, wind.dir.z);
  waterUniforms.uWaterTime.value = wind.time;
  waterUniforms.uWaterRough.value = Math.min(1, wind.speed / ROUGH_WIND);
}

waterMaterial.onBeforeCompile = (shader) => {
  Object.assign(shader.uniforms, waterUniforms);
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nvarying vec2 vWaterPos;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvWaterPos = (modelMatrix * vec4(transformed, 1.0)).xz;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>
varying vec2 vWaterPos;
uniform float uWaterTime;
uniform vec2 uWaterShift;
uniform vec2 uWaterDir;
uniform float uWaterRough;
float waterHash(vec2 c) { return fract(sin(dot(c, vec2(127.1, 311.7))) * 43758.5453); }
float waterNoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(waterHash(i), waterHash(i + vec2(1.0, 0.0)), f.x),
             mix(waterHash(i + vec2(0.0, 1.0)), waterHash(i + vec2(1.0, 1.0)), f.x), f.y);
}`)
    .replace('#include <color_fragment>', `#include <color_fragment>
  float waterCrest = 0.5;
  float waterGlint = 0.0;
{
  vec2 p = vWaterPos;
  vec2 across = vec2(-uWaterDir.y, uWaterDir.x);
  // Two trains of bands, one downwind and one across it, their phase
  // wandering with a slow noise; stepped to four tones, a palette's.
  float wander = waterNoise(p / 70.0) * 6.2831;
  float a = sin(dot(p - uWaterShift, uWaterDir) * 0.42 + wander);
  float b = sin(dot(p, across) * 0.19 + uWaterTime * 0.3 + wander * 0.6);
  float v = (a * 0.65 + b * 0.35) * 0.5 + 0.5;
  waterCrest = v;
  float tone = mix(0.9, 1.08, floor(v * 3.999) / 3.0);
  diffuseColor.rgb *= mix(1.0, tone, 0.4 + 0.6 * uWaterRough);
  // Subtle grazing Fresnel lift so distant water reflects the sky's lightness.
  vec3 viewDir = normalize(vViewPosition);
  float fresnel = pow(1.0 - clamp(abs(viewDir.y), 0.0, 1.0), 3.2);
  diffuseColor.rgb = mix(diffuseColor.rgb, diffuseColor.rgb * 1.18 + vec3(0.04, 0.06, 0.08), fresnel * 0.45);
  // Glints: in a few cells on a slant, a short fleck across the wind
  // somewhere in the cell, flashing briefly in turn.
  vec2 q = vec2(dot(p, vec2(0.866, 0.5)), dot(p, vec2(-0.5, 0.866)) * 1.7) / 2.6;
  vec2 cell = floor(q);
  float h = waterHash(cell);
  float g = fract(h * 13.0 + uWaterTime * (0.07 + h * 0.08));
  vec2 f = fract(q) - 0.5 - (vec2(waterHash(cell + 3.1), waterHash(cell + 7.7)) - 0.5) * 0.6;
  float fleck = (1.0 - smoothstep(0.05, 0.09, abs(dot(f, across)))) * (1.0 - smoothstep(0.012, 0.025, abs(dot(f, uWaterDir))));
  float glint = step(0.7, h) * (1.0 - smoothstep(0.0, 0.035, g)) * fleck;
  // Near, where one is a glint: far off, a field of them is confetti.
  glint *= 1.0 - smoothstep(20.0, 70.0, length(vViewPosition));
  waterGlint = glint;
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.92, 0.98, 1.0), glint * (0.28 + 0.28 * uWaterRough));
}`)
    .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
  roughnessFactor = mix(0.46, 0.2, clamp(waterCrest * 0.45 + waterGlint * 0.8, 0.0, 1.0));`);
};

export interface VegetationChunk {
  flora: InstancedMesh[];
  /** Pools of lamplight on the ground, visible at night. */
  pools: InstancedMesh | undefined;
  water: Mesh | undefined;
  roads: Mesh | undefined;
  /** Parapets and deck edges along bridges. */
  bridges: Mesh | undefined;
  /** Tunnel tubes, unlit, with their lamps. */
  tunnels?: Mesh;
  /** Rivers, streams and canals drawn from their centrelines. */
  waterways: Mesh | undefined;
  piers: Mesh | undefined;
  /** Pitches, courts and pools. */
  surfaces: Mesh | undefined;
  /** In pieces, to be uploaded a few a frame. See `createHouseMeshes`. */
  buildings: Mesh[];
  /** Name boards on buildings and fuel pylons, one atlas for the tile. */
  signs: Mesh | undefined;
  /** Helipads: marked discs, on the ground or on roofs. */
  helipads: Mesh | undefined;
  /** Tailpipes on the parked cars that burn fuel. Added by the renderer, which knows where they are. */
  exhausts?: InstancedMesh;
}

/**
 * One bounding sphere for everything in a tile, from its extent and height
 * range: exact enough for culling, and free — computing a sphere from a
 * building buffer's four hundred thousand vertices was most of a tile's
 * main-thread time.
 */
function tileSphere(chunk: PlacedVectorChunk): Sphere {
  const b = tileBounds(chunk.key);
  const lat = (b.north + b.south) / 2;
  const width = (b.east - b.west) * 111320 * Math.cos((lat * Math.PI) / 180);
  const depth = (b.north - b.south) * 111320;
  const centre = new Vector3(width / 2, (chunk.minY + chunk.maxY) / 2, depth / 2);
  // Past the tile by its buffer: features reach a little over its edges.
  const radius = Math.hypot(width * 0.6, depth * 0.6, (chunk.maxY - chunk.minY) / 2 + 5);
  return new Sphere(centre, radius);
}

export function buildVegetation(chunk: PlacedVectorChunk): VegetationChunk {
  const sphere = tileSphere(chunk);
  const withSphere = <T extends Mesh | undefined>(mesh: T): T => {
    if (mesh !== undefined) mesh.geometry.boundingSphere = sphere.clone();
    return mesh;
  };
  // Boulders take the local rock colour and grass tufts the local grass, so a
  // desert's rocks are red sandstone and a tundra's are grey granite.
  const bounds = tileBounds(chunk.key);
  const weights = biomeWeights(seaLevelClimate((bounds.north + bounds.south) / 2, (bounds.west + bounds.east) / 2));
  return {
    flora: buildFlora(chunk.treePositions, chunk.treeRotations, chunk.treeScales, chunk.treeSpecies, {
      rock: blendedTone(weights, 'rock'),
      grass: blendedTone(weights, 'ground'),
    }, chunk.treePaints, chunk.treeTilts),
    water: withSphere(buildWater(chunk)),
    pools: buildLightPools(chunk.treePositions, chunk.treeRotations, chunk.treeSpecies),
    roads: withSphere(buildRoads(chunk)),
    bridges: withSphere(buildBridges(chunk)),
    tunnels: withSphere(buildTunnels(chunk)),
    waterways: withSphere(buildIndexed(chunk.waterLinePositions, chunk.waterLineIndices, chunk.normals.waterLines, waterMaterial, 1)),
    piers: withSphere(buildIndexed(chunk.pierPositions, chunk.pierIndices, chunk.normals.piers, pierMaterial, 2)),
    surfaces: withSphere(buildSurfaceMesh(chunk)),
    // Buildings come out of the worker already merged, coloured and wound, so
    // there is nothing left to do on the main thread but wrap the buffers.
    buildings: createHouseMeshes(
      chunk.buildingPositions,
      chunk.buildingColors,
      chunk.buildingIndices,
      chunk.buildingGlow,
      sphere.clone(),
    ),
    signs: buildSigns(chunk.signs),
    helipads: withSphere(buildHelipads(chunk.helipads)),
  };
}

/**
 * A plain indexed mesh. Its normals come with it, computed where the ribbon
 * was draped — in the placement worker — and its bounding sphere is set by
 * the caller from the tile, so wrapping it here is only wrapping.
 */
function buildIndexed(
  positions: Float32Array,
  indices: Uint32Array,
  normals: Float32Array,
  material: MeshStandardMaterial,
  renderOrder: number,
): Mesh | undefined {
  if (indices.length === 0) return undefined;
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new BufferAttribute(normals, 3));
  geometry.setIndex(new BufferAttribute(indices, 1));
  const mesh = new Mesh(geometry, material);
  mesh.renderOrder = renderOrder;
  return mesh;
}

/** Road ribbons with the per-vertex data the road shader draws from. */
function buildRoads(chunk: PlacedVectorChunk): Mesh | undefined {
  const mesh = buildIndexed(chunk.roadPositions, chunk.roadIndices, chunk.normals.roads, roadMaterial, 2);
  mesh?.geometry.setAttribute('aRoad', new BufferAttribute(chunk.roadAttributes, 4));
  // Roads and paths are plowed: the snow keeps off them (see snowField.ts).
  mesh?.layers.enable(CLEARED_LAYER);
  return mesh;
}

function buildSurfaceMesh(chunk: PlacedVectorChunk): Mesh | undefined {
  const mesh = buildIndexed(chunk.surfacePositions, chunk.surfaceIndices, chunk.normals.surfaces, surfaceMaterial, 3);
  mesh?.geometry.setAttribute('aSurface', new BufferAttribute(chunk.surfaceAttributes, 4));
  // Plowed where they are parking bays (see snowField.ts).
  mesh?.layers.enable(SURFACE_LAYER);
  return mesh;
}

/**
 * Bridge structure — arches, spandrels, piers, parapets — built in the core
 * (`core/build/bridges.ts`) and only wrapped here.
 */
function buildBridges(chunk: PlacedVectorChunk): Mesh | undefined {
  if (chunk.bridgeIndices.length === 0) return undefined;
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(chunk.bridgePositions, 3));
  geometry.setAttribute('color', new BufferAttribute(chunk.bridgeColors, 3));
  geometry.setIndex(new BufferAttribute(chunk.bridgeIndices, 1));
  return new Mesh(geometry, bridgeMaterial);
}

/** Tunnel tubes, built in the core (`core/build/tunnelmesh.ts`) and only wrapped here. */
function buildTunnels(chunk: PlacedVectorChunk): Mesh | undefined {
  if (chunk.tunnelIndices === undefined || chunk.tunnelIndices.length === 0) return undefined;
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(chunk.tunnelPositions!, 3));
  geometry.setAttribute('color', new BufferAttribute(chunk.tunnelColors!, 3));
  geometry.setIndex(new BufferAttribute(chunk.tunnelIndices, 1));
  return new Mesh(geometry, tunnelMaterial);
}

function buildWater(chunk: PlacedVectorChunk): Mesh | undefined {
  if (chunk.waterIndices.length === 0) return undefined;
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(chunk.waterPositions, 3));
  geometry.setAttribute('normal', new BufferAttribute(chunk.normals.water, 3));
  geometry.setIndex(new BufferAttribute(chunk.waterIndices, 1));
  const mesh = new Mesh(geometry, waterMaterial);
  mesh.renderOrder = 1;
  // No snow lies on open water.
  mesh.layers.enable(CLEARED_LAYER);
  return mesh;
}

export function disposeVegetation(chunk: VegetationChunk): void {
  // The geometries and materials are shared module-level singletons, so only
  // the per-chunk instance buffers and the water geometry are ours to release.
  chunk.flora.forEach(mesh => mesh.dispose());
  chunk.pools?.dispose();
  chunk.water?.geometry.dispose();
  chunk.roads?.geometry.dispose();
  chunk.bridges?.geometry.dispose();
  chunk.tunnels?.geometry.dispose();
  chunk.waterways?.geometry.dispose();
  chunk.piers?.geometry.dispose();
  chunk.surfaces?.geometry.dispose();
  for (const piece of chunk.buildings) disposeHouseChunk(piece);
  disposeSigns(chunk.signs);
  chunk.helipads?.geometry.dispose();
  chunk.exhausts?.dispose();
}
